import type { CoinGeckoSelection } from '#core/utils/coingecko-selection'
import { useAuth } from '#auth/composables/useAuth'
import { reactive, ref, useRoute } from '#imports'

export type LoginModalView =
  | 'login'
  | 'forgot-password'
  | 'advisor-form'
  | 'advisor-success'
  | 'register'
  | 'register-details'
  | 'register-finalizing'

export type LoginModalVariant = 'default' | 'continue'
export type LoginModalCloseReason = 'success' | 'dismiss'

export interface LoginModalCloseEvent {
  variant: LoginModalVariant
  reason: LoginModalCloseReason
}

export type TokenStatus = 'pre-tge' | 'listed' | 'no-token'

export interface RegisterData {
  email: string
  password: string
  tokenStatus: TokenStatus
  coin: CoinGeckoSelection | null
  projectName: string
  ticker: string
  firstName: string
  lastName: string
  telegram: string
}

function emptyRegisterData(): RegisterData {
  return {
    email: '',
    password: '',
    tokenStatus: 'listed',
    coin: null,
    projectName: '',
    ticker: '',
    firstName: '',
    lastName: '',
    telegram: '',
  }
}

/**
 * Global state for the login modal so it can be opened from anywhere in the app
 * (mirrors the module-level pattern used by `useAppSlideover`).
 *
 * @example
 * const { open } = useLoginModal()
 * open()
 */
const isOpen = ref(false)
const view = ref<LoginModalView>('login')
const variant = ref<LoginModalVariant>('default')

const mandatory = ref(false)
const registerData = reactive<RegisterData>(emptyRegisterData())
let lastCloseEvent: LoginModalCloseEvent | null = null

export function useLoginModal() {
  function captureOrigin() {
    useAuth().setRedirectTo(useRoute().fullPath)
  }

  function open() {
    captureOrigin()
    lastCloseEvent = null
    mandatory.value = false
    variant.value = 'default'
    view.value = 'login'
    isOpen.value = true
  }

  function openContinue() {
    captureOrigin()
    lastCloseEvent = null
    mandatory.value = false
    variant.value = 'continue'
    view.value = 'login'
    isOpen.value = true
  }

  function openAdvisor() {
    captureOrigin()
    lastCloseEvent = null
    mandatory.value = false
    view.value = 'advisor-form'
    isOpen.value = true
  }

  function setView(next: LoginModalView) {
    view.value = next
  }

  function startRegister() {
    Object.assign(registerData, emptyRegisterData())
    view.value = 'register'
  }

  /**
   * Reopens the modal directly on the "register details" step for a user who has
   * already authenticated via Google OAuth but hasn't finalized registration yet.
   * Unlike `open()` it does NOT capture a new origin — the caller is already on the
   * page the user opened the modal from (restored after the OAuth redirect).
   */
  function openOAuthFinalize(prefill?: Partial<Pick<RegisterData, 'firstName' | 'lastName'>>) {
    Object.assign(registerData, emptyRegisterData())
    if (prefill?.firstName) {
      registerData.firstName = prefill.firstName
    }
    if (prefill?.lastName) {
      registerData.lastName = prefill.lastName
    }
    lastCloseEvent = null
    mandatory.value = true
    variant.value = 'default'
    view.value = 'register-details'
    isOpen.value = true
  }

  function close() {
    if (!lastCloseEvent) {
      lastCloseEvent = { variant: variant.value, reason: 'dismiss' }
    }
    isOpen.value = false
    mandatory.value = false
    variant.value = 'default'
  }

  function closeAfterSuccess() {
    lastCloseEvent = { variant: variant.value, reason: 'success' }
    close()
  }

  function consumeCloseEvent(): LoginModalCloseEvent | null {
    const event = lastCloseEvent
    lastCloseEvent = null
    return event
  }

  return {
    isOpen,
    view,
    variant,
    mandatory,
    registerData,
    open,
    openContinue,
    openAdvisor,
    openOAuthFinalize,
    setView,
    startRegister,
    close,
    closeAfterSuccess,
    consumeCloseEvent,
  }
}
